import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";

import { formatCurrency } from "../../core/currency.core";
import localeCore from "../../core/locale.core";
import { getCustomOptions } from "../../core/product.core";
import { useTranslation } from "../../core/translation.core";
import useShop from "../../hooks/useShop.hook";
import { CurrencyString, Product, ProductType } from "../../types/product";
import { ShopTiny } from "../../types/shop";
import OutlineLink from "../form/outlineLink.comp";
import { EditingMode } from "../reuseables/form.comp";

interface Props {
  editingMode: EditingMode;
  parentProduct?: Product;
  productType: ProductType;
}

export default function CustomProductItems({ editingMode, parentProduct, productType }: Props) {
  const { t, msg } = useTranslation();

  if (productType !== "custom") return null;
  if (editingMode !== EditingMode.edit) return null;
  if (!parentProduct) return null;

  return (
    <div>
      <div className="block text-base font-semibold mb-2">
        {t(msg.product.customOptions)}&nbsp;{" "}
        <OutlineLink id="add-option" href={`/product/custom-options/${parentProduct.id}`} text={t(msg.form.edit)} />
      </div>
      <div className="overflow-x-scroll">
        <CustomOptionListEshop />
      </div>
    </div>
  );
}

function CustomOptionListEshop() {
  const router = useRouter();
  const { selectedShop: shop } = useShop();

  const {
    data: customOptions,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["productCustomOptions", router.query.id],
    queryFn: () => getCustomOptions(router.query.id as string),
    enabled: router.isReady && router.query.id !== undefined,
  });

  if (isError) return null;
  if (isLoading) return null;
  if (!customOptions) return null;
  if (!shop) return null;

  const mappedOptions = customOptions.map((c) => ({
    ...c,
    options: c.items
      .map((i) => localeCore.getLocaleValue(i.name, router.locale))
      .filter((o) => o !== null && o !== undefined) as string[],
  }));

  return (
    <div id="custom-options-list" className="[&>*:not(:last-child)]:border-b">
      {mappedOptions?.map((c, idx) => (
        <ListItem key={idx} idx={idx} options={c.options} shop={shop} price={c.price} />
      ))}
    </div>
  );
}

interface ListItemProps {
  idx: number;
  shop: ShopTiny;
  options: string[];
  price: number | null;
}

function ListItem({ idx, shop, options, price }: ListItemProps) {
  return (
    <div className="w-full grid grid-cols-[40px_repeat(4,_minmax(150px,_1fr))] items-center">
      <p className="text-xs w-10">#{idx + 1}</p>
      {options.map((o, idx2) => (
        <div key={idx2}>{o}</div>
      ))}
      <p>{price ? formatCurrency(price, shop?.defaultCurrency as CurrencyString) : ""}</p>
    </div>
  );
}
